import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { findWordPathInGrid, getWordAtPath } from '../../../utils/wordSearchGrid';

export interface FindTheWordsWord {
  id: number;
  word: string;
}

export interface FindTheWordsPlayerProps {
  gridLetters: string;
  rows: number;
  cols: number;
  words: FindTheWordsWord[];
  foundIds: number[];
  onFoundIdsChange: (ids: number[]) => void;
  disabled?: boolean;
  hint?: string;
}

export function serializeFoundIds(ids: number[]): string {
  return ids.join(',');
}

function normalizeWord(w: string): string {
  return String(w ?? '')
    .replace(/\s+/g, '')
    .trim()
    .toLocaleLowerCase();
}

function lineFromTo(
  start: { r: number; c: number },
  end: { r: number; c: number }
): Array<{ r: number; c: number }> | null {
  const dr = end.r - start.r;
  const dc = end.c - start.c;
  if (dr === 0 && dc === 0) return [start];
  const absR = Math.abs(dr);
  const absC = Math.abs(dc);
  if (dr !== 0 && dc !== 0 && absR !== absC) return null;
  const steps = Math.max(absR, absC);
  const stepR = dr === 0 ? 0 : dr / steps;
  const stepC = dc === 0 ? 0 : dc / steps;
  if (!Number.isInteger(stepR) || !Number.isInteger(stepC)) return null;
  const cells: Array<{ r: number; c: number }> = [];
  for (let i = 0; i <= steps; i++) {
    cells.push({ r: start.r + i * stepR, c: start.c + i * stepC });
  }
  return cells;
}

function cellFromPoint(clientX: number, clientY: number): { r: number; c: number } | null {
  const el = document.elementFromPoint(clientX, clientY);
  const cell = el?.closest?.('[data-ftw-r][data-ftw-c]') as HTMLElement | null;
  if (!cell) return null;
  const r = Number(cell.dataset.ftwR);
  const c = Number(cell.dataset.ftwC);
  if (!Number.isFinite(r) || !Number.isFinite(c)) return null;
  return { r, c };
}

function cellKey(r: number, c: number): string {
  return `${r},${c}`;
}

export function FindTheWordsPlayer({
  gridLetters,
  rows,
  cols,
  words,
  foundIds,
  onFoundIdsChange,
  disabled,
  hint,
}: FindTheWordsPlayerProps) {
  const [path, setPath] = useState<Array<{ r: number; c: number }>>([]);
  const [foundPaths, setFoundPaths] = useState<Record<number, Array<{ r: number; c: number }>>>({});
  const selectingRef = useRef(false);
  const startRef = useRef<{ r: number; c: number } | null>(null);
  const pathRef = useRef<Array<{ r: number; c: number }>>([]);
  const foundSet = new Set(foundIds);
  const gridRef = useRef<HTMLDivElement | null>(null);

  // Rebuild permanent highlights from foundIds + words (e.g. resume / remount).
  useEffect(() => {
    setFoundPaths((prev) => {
      const next: Record<number, Array<{ r: number; c: number }>> = {};
      for (const id of foundIds) {
        if (prev[id]) {
          next[id] = prev[id];
          continue;
        }
        const word = words.find((w) => w.id === id);
        if (!word) continue;
        const foundPath = findWordPathInGrid(gridLetters, rows, cols, word.word);
        if (foundPath) next[id] = foundPath;
      }
      return next;
    });
  }, [foundIds, gridLetters, rows, cols, words]);

  const foundHighlightKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const id of foundIds) {
      const cells = foundPaths[id];
      if (!cells) continue;
      for (const cell of cells) keys.add(cellKey(cell.r, cell.c));
    }
    return keys;
  }, [foundIds, foundPaths]);

  const letterAt = (r: number, c: number) => {
    const idx = r * cols + c;
    return gridLetters[idx] ?? '';
  };

  const finishSelection = useCallback(
    (cells: Array<{ r: number; c: number }>) => {
      selectingRef.current = false;
      startRef.current = null;
      pathRef.current = [];
      setPath([]);
      if (disabled || cells.length < 1) return;

      const forward = normalizeWord(getWordAtPath(gridLetters, rows, cols, cells));
      const reverse = normalizeWord(
        getWordAtPath(gridLetters, rows, cols, [...cells].reverse())
      );
      const match = words.find((w) => {
        if (foundIds.includes(w.id)) return false;
        const nw = normalizeWord(w.word);
        return nw.length > 0 && (nw === forward || nw === reverse);
      });
      if (match) {
        const storedPath =
          normalizeWord(match.word) === reverse ? [...cells].reverse() : cells;
        setFoundPaths((prev) => ({ ...prev, [match.id]: storedPath }));
        onFoundIdsChange([...foundIds, match.id]);
      }
    },
    [disabled, foundIds, gridLetters, onFoundIdsChange, rows, cols, words]
  );

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!selectingRef.current || !startRef.current) return;
      const cell = cellFromPoint(e.clientX, e.clientY);
      if (!cell) return;
      const line = lineFromTo(startRef.current, cell);
      if (!line) return;
      pathRef.current = line;
      setPath(line);
    };
    const onUp = () => {
      if (!selectingRef.current) return;
      finishSelection(pathRef.current);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
    };
  }, [finishSelection]);

  const onCellPointerDown = (r: number, c: number, e: React.PointerEvent) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    selectingRef.current = true;
    startRef.current = { r, c };
    pathRef.current = [{ r, c }];
    setPath([{ r, c }]);
    try {
      gridRef.current?.setPointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const pathKey = new Set(path.map((p) => cellKey(p.r, p.c)));

  const wordChips = (
    <ul className="flex flex-wrap gap-2.5 justify-center">
      {words.map((w) => {
        const found = foundSet.has(w.id);
        return (
          <li key={w.id} className="relative inline-flex">
            <span
              className={`inline-flex min-w-[2.75rem] items-center justify-center px-2.5 py-1 rounded-md text-sm border text-center ${
                found
                  ? 'border-green-400 dark:border-green-700 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200'
                  : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200'
              }`}
              dir="auto"
            >
              {w.word}
            </span>
            {found ? (
              <span
                className="pointer-events-none absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-green-600 text-white ring-1 ring-white dark:ring-gray-900"
                aria-hidden
              >
                <Check className="h-2.5 w-2.5" strokeWidth={3} />
              </span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className="space-y-3 w-full">
      {hint ? (
        <p className="text-xs text-gray-600 dark:text-gray-400" dir="auto">
          {hint}
        </p>
      ) : null}
      {wordChips}
      {/* dir=ltr keeps column order fixed regardless of UI language */}
      <div className="w-full flex justify-center" dir="ltr">
        <div
          ref={gridRef}
          className="grid gap-0.5 p-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 select-none touch-none w-full md:w-4/5"
          style={{
            gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
            touchAction: 'none',
          }}
        >
          {Array.from({ length: rows }, (_, r) =>
            Array.from({ length: cols }, (_, c) => {
              const selected = pathKey.has(cellKey(r, c));
              const permanentlyFound = foundHighlightKeys.has(cellKey(r, c));
              return (
                <div
                  key={`${r}-${c}`}
                  role="button"
                  tabIndex={disabled ? -1 : 0}
                  data-ftw-r={r}
                  data-ftw-c={c}
                  aria-label={`Cell ${r + 1}, ${c + 1}: ${letterAt(r, c)}`}
                  onPointerDown={(e) => onCellPointerDown(r, c, e)}
                  className={`aspect-square flex items-center justify-center text-sm font-semibold rounded cursor-pointer border border-gray-400 dark:border-gray-600 ${
                    selected
                      ? 'bg-blue-600 text-white dark:bg-blue-500 border-blue-600 dark:border-blue-500'
                      : permanentlyFound
                        ? 'bg-green-200 dark:bg-green-800/60 text-green-900 dark:text-green-100 border-green-500 dark:border-green-600'
                        : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-blue-50 dark:hover:bg-gray-600'
                  } ${disabled ? 'opacity-70 cursor-default' : ''}`}
                >
                  <span dir="auto">{letterAt(r, c)}</span>
                </div>
              );
            })
          )}
        </div>
      </div>
      {wordChips}
      <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
        {foundIds.length} / {words.length}
      </p>
    </div>
  );
}
